export const restBlockDocumentationMarkdown = `
# REST API Call

This block makes a REST API call to the specified endpoint.

## Secure Headers

A secure header appends a secret value to the request headers. The secret value is stored securely using Google Cloud's Secret Manager.
Create a secure header [here](https://app.notablehealth.com/staff/app#/agents/secureHeaders).
Note: The endpoint's domain must match the secure header's domain exactly. I.e. *https://subdomain.google.com* does not match *https://google.com*.

## Examples
### GET Request
- Method: GET
- Endpoint: https:&#xFEFF;//jsonplaceholder.typicode.com/todos/1
### POST Request
- Method: POST
- Endpoint: https&#xFEFF;://jsonplaceholder.typicode.com/posts
- Body:
~~~json
{
  "title": "foo",
  "body": "bar",
  "userId": 1
}
~~~

## Important Tips

### 1. Error Handling
*  Throws an error if the response status code is 400+
*  Body content that is not in a JSON format will cause block execution to fail
*  Error paths can be used to continue flow execution if the request fails

### 2. Response Body Mode
* The response body mode determines how the response body is handled.
* **AutoDetect** attempts to parse the response as JSON if possible; otherwise, it returns the raw text.
* **Parsed Base64** parses the entire response as a base64-encoded string, which is useful for handling binary files (e.g., PDFs or images).

### 3. Best Practices
*  Use a secure header to store sensitive headers instead of plaintext
*  Test your block out with different inputs
*  Consider adding an error handle to your block to continue execution (mouse over the bottom middle of a block) if the response status code is invalid

### 4. Metadata
*  Block metadata exposes several parameters to facilitate debugging.
*  The response status code (metadata.statusCode) and status text (metadata.statusText) are both returned in the block metadata.
*  Selected response headers are also added to the metadata (metadata.headers).

## Common Use Cases
1. **Two-legged OAuth**: Used for server-to-server authorization without user interaction.
*  Create a secure basic auth header (see **Secure Headers** above) for the authorization server
*  Create a REST Block (using the secure header) to fetch an ephemeral bearer token from the authorization server. 
*  Create a downstream REST Block to access the resource store using the bearer token returned from the prior block.
    If your token expires, repeat the previous step to fetch a fresh token.

2. **Pagination**: Chain multiple REST blocks to retrieve paginated data
*  Fetch the first page with a REST Block.
*  Look for pagination details in the response (e.g., nextPageURL, page=2, offset=50, etc.).
*  Use the next page info from the previous response to configure a new REST Block.
*  Use conditional blocks to determine if there are more pages to retrieve.
*  Use the iterator block to loop through the pages and make an API request for each.

3. **Polling**: Periodically checking an external API for updated data
*  Chain REST blocks with Wait blocks to periodically hit an endpoint with a specified interval

## Limitations
1. **Private IP restriction**: Requests to private IP addresses are blocked to prevent internal network abuse.
2. **Timeout limit**: Each request is capped by a 60-second timeout. After that, a timeout error is thrown.
3. **Large Responses**: Handling very large files or base64-encoded data can be memory-intensive. Entire response bodies (including PDF or image data) are processed in-memory.

## Expected Side Effects
1. **Error handling**: If the HTTP status code is 400 or higher, or if JSON parsing fails, the block will throw an error unless you configure an error path to handle it gracefully.
2. **Increased storage usage**: Using BASE64 to handle binary data results in a larger string size. If you store or pass this base64 string downstream, it may increase both payload size and memory consumption in your flow.
`;
